/**
 * Registers handlers for the catalog's `view_notifications` and
 * `close_notifications` actions — opens / closes the host's
 * notifications drawer.
 *
 * The catalog (server-side, in `noki-voice-copilot/host_actions.py`)
 * owns each action's name, description, classification, and parameter
 * schema (no parameters here). This module only declares "noki-fe
 * implements those actions" and provides the handlers that run when
 * the agent dispatches them. Each handler dispatches the matching
 * existing redux action that the Topbar's bell-icon button already
 * uses — no new redux surface introduced.
 *
 * Both reducer paths are pure setters (`notificationModalOpen` =
 * true / false), so calling either twice is harmless.
 *
 * Mirrors the shape of `useCopilotThemeAction` and
 * `useCopilotNavigation` so `NokiCopilot.jsx` can call this
 * single hook from the top of its component body.
 */

import { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  closeNotificationModal,
  openNotificationModal,
} from "redux/actions/AppActions";

import { useCopilotAction } from "./actionRegistry";

const VIEW_NOTIFICATIONS_DESCRIPTOR = Object.freeze({
  name: "view_notifications",
  description: "Implemented by noki-fe (redux openNotificationModal).",
  classification: "READ",
  parameters: [],
});

const CLOSE_NOTIFICATIONS_DESCRIPTOR = Object.freeze({
  name: "close_notifications",
  description: "Implemented by noki-fe (redux closeNotificationModal).",
  classification: "READ",
  parameters: [],
});

export function useCopilotNotificationsAction() {
  const dispatch = useDispatch();
  const viewDescriptor = useMemo(() => VIEW_NOTIFICATIONS_DESCRIPTOR, []);
  const closeDescriptor = useMemo(() => CLOSE_NOTIFICATIONS_DESCRIPTOR, []);

  useCopilotAction(viewDescriptor, async () => {
    // eslint-disable-next-line no-console
    console.info("[copilot] view_notifications.invoke");
    dispatch(openNotificationModal());
    // eslint-disable-next-line no-console
    console.info("[copilot] view_notifications.dispatched");
    return { opened: true };
  });

  useCopilotAction(closeDescriptor, async () => {
    // eslint-disable-next-line no-console
    console.info("[copilot] close_notifications.invoke");
    dispatch(closeNotificationModal());
    // eslint-disable-next-line no-console
    console.info("[copilot] close_notifications.dispatched");
    return { closed: true };
  });
}
